/** Query keys for shipping-lines financial statement flows (shareable / refresh-safe URLs). */

export const FS_QUERY = {
  SHIPPING_LINE_NAME: 'name',
  SHIPPING_LINE_ID: 'shipping_line_id',
  SOA_ID: 'soa_id',
  /** Comma-separated SOA IDs for multi-SOA flows (e.g. create invoice). */
  SOA_IDS: 'soa_ids',
  INVOICE_ID: 'invoice_id',
  BILLING_STATEMENT_ID: 'billing_id',
  EXPECTED_DATE_FROM: 'from',
  EXPECTED_DATE_TO: 'to',
} as const

export type FinancialStatementsQueryInput = {
  shippingLineName?: string
  soaId?: number
  /** One or more SOA IDs (serialized as soa_ids=1,2,3). */
  soaIds?: number[]
  expectedDateFrom?: string
  expectedDateTo?: string
}

export function buildFinancialStatementsQuery(input: FinancialStatementsQueryInput): string {
  const p = new URLSearchParams()
  if (input.shippingLineName) {
    p.set(FS_QUERY.SHIPPING_LINE_NAME, input.shippingLineName)
  }
  if (input.soaIds != null && input.soaIds.length > 0) {
    const unique = [...new Set(input.soaIds.filter((id) => Number.isFinite(id)))]
    if (unique.length > 0) {
      p.set(FS_QUERY.SOA_IDS, unique.join(','))
    }
  } else if (input.soaId != null && Number.isFinite(input.soaId)) {
    p.set(FS_QUERY.SOA_ID, String(input.soaId))
  }
  if (input.expectedDateFrom) {
    p.set(FS_QUERY.EXPECTED_DATE_FROM, input.expectedDateFrom)
  }
  if (input.expectedDateTo) {
    p.set(FS_QUERY.EXPECTED_DATE_TO, input.expectedDateTo)
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

export function shippingLineNameFromSearchParams(sp: URLSearchParams): string {
  return sp.get(FS_QUERY.SHIPPING_LINE_NAME) ?? ''
}

export function shippingLineIdFromSearchParams(sp: URLSearchParams): string | undefined {
  const raw = sp.get(FS_QUERY.SHIPPING_LINE_ID)
  if (raw == null || raw === '') return undefined
  return raw
}

export function soaIdFromSearchParams(sp: URLSearchParams): number | undefined {
  const raw = sp.get(FS_QUERY.SOA_ID)
  if (raw == null || raw === '') return undefined
  const n = parseInt(raw, 10)
  return Number.isFinite(n) ? n : undefined
}

/** Parses `soa_ids=1,2,3` (also works for a single id: `soa_ids=1`). */
export function soaIdsFromSearchParams(sp: URLSearchParams): number[] {
  const rawList = sp.get(FS_QUERY.SOA_IDS)
  if (rawList == null || rawList.trim() === '') return []
  const ids = rawList
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => Number.isFinite(n))
  return [...new Set(ids)]
}

export function invoiceIdFromSearchParams(sp: URLSearchParams): number | undefined {
  const raw = sp.get(FS_QUERY.INVOICE_ID)
  if (raw == null || raw === '') return undefined
  const n = parseInt(raw, 10)
  return Number.isFinite(n) ? n : undefined
}

export function billingStatementIdFromSearchParams(sp: URLSearchParams): number | undefined {
  const raw = sp.get(FS_QUERY.BILLING_STATEMENT_ID)
  if (raw == null || raw === '') return undefined
  const n = parseInt(raw, 10)
  return Number.isFinite(n) ? n : undefined
}

export function expectedDateFromSearchParams(sp: URLSearchParams): string | undefined {
  const v = sp.get(FS_QUERY.EXPECTED_DATE_FROM)
  return v && v.length > 0 ? v : undefined
}

export function expectedDateToSearchParams(sp: URLSearchParams): string | undefined {
  const v = sp.get(FS_QUERY.EXPECTED_DATE_TO)
  return v && v.length > 0 ? v : undefined
}
